import { queryClient } from 'v2/configs/rq-setup'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import { useTaskStore } from 'v2/state/task'
import type { RTask, RTaskActivity, TaskList } from 'v2/types/api/conversations'

interface UpdateTasksCacheParams {
  conversationId: string
  operation: 'create' | 'update' | 'delete'
  taskUuid?: string
  data?: RTask
}

const updateTasksCallback = (
  operation: 'create' | 'update' | 'delete',
  oldTasks: TaskList = { tasks: [], total_count: 0 },
  data?: RTask,
  taskUuid?: string,
) => {
  switch (operation) {
    case 'create':
      return {
        tasks: [data as RTask, ...oldTasks.tasks],
        total_count: oldTasks.total_count + 1,
      }
    case 'update':
      return {
        tasks: oldTasks.tasks.map((task) => (task.uuid === taskUuid ? (data as RTask) : task)),
        total_count: oldTasks.total_count,
      }
    case 'delete':
      return {
        tasks: oldTasks.tasks.filter((task) => task.uuid !== taskUuid),
        total_count: oldTasks.total_count - 1,
      }
    default:
      return oldTasks
  }
}

export function updateTaskInQueryCache({
  conversationId,
  operation,
  taskUuid,
  data,
}: UpdateTasksCacheParams) {
  // Update tasks in conversation tasks query for right panel
  queryClient.setQueryData(
    conversationQueryKeys().conversationTasks(conversationId),
    (oldTasks: TaskList) => updateTasksCallback(operation, oldTasks, data, taskUuid),
  )

  // Update tasks in conversation tasks query for list view
  const { currentView, viewFilters } = useTaskStore.getState()
  const currentViewFilters = viewFilters[currentView]

  queryClient.setQueryData(
    conversationQueryKeys().tasks(currentView, currentViewFilters),
    (oldTasks: TaskList) => updateTasksCallback(operation, oldTasks, data, taskUuid),
  )
}

interface UpdateTaskActivitiesCacheParams {
  taskUuid: string
  operation: 'create' | 'update' | 'delete'
  activityId?: string
  data?: RTaskActivity
}

export function updateTaskActivityInQueryCache({
  taskUuid,
  operation,
  activityId,
  data,
}: UpdateTaskActivitiesCacheParams) {
  queryClient.setQueryData(
    conversationQueryKeys().taskActivities(taskUuid),
    (oldActivities: RTaskActivity[] = []) => {
      switch (operation) {
        case 'create':
          return [...oldActivities, data as RTaskActivity]
        case 'update':
          return oldActivities.map((activity) =>
            activity.activity_id === activityId ? (data as RTaskActivity) : activity,
          )
        case 'delete':
          return oldActivities.filter((activity) => activity.activity_id !== activityId)
        default:
          return oldActivities
      }
    },
  )
}
